import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { LoadingFallback, PageLoadingFallback } from "./components/LoadingFallback";
import ProtectedRoute from "./components/ProtectedRoute";
import { DashboardLayout } from "./components/DashboardLayout";
import { GlobalMutationIndicator } from "./components/GlobalMutationIndicator";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { usePrefetch } from "./hooks/usePrefetch";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

// Lazy load pages for code splitting
const Landing = lazy(() => import("./pages/Landing"));
const Auth = lazy(() => import("./pages/Auth"));
const Upload = lazy(() => import("./pages/Upload"));
const Preview = lazy(() => import("./pages/Preview"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Ace = lazy(() => import("./pages/Ace"));
const Courses = lazy(() => import("./pages/Courses"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const CalendarView = lazy(() => import("./pages/CalendarView"));
const Grades = lazy(() => import("./pages/Grades"));
const Materials = lazy(() => import("./pages/Materials"));
const MaterialDetail = lazy(() => import("./pages/MaterialDetail"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Paywall = lazy(() => import("./pages/Paywall"));
const Account = lazy(() => import("./pages/Account"));
const Contact = lazy(() => import("./pages/Contact"));

const Help = lazy(() => import("./pages/Help"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Socials = lazy(() => import("./pages/Socials"));
const NotFound = lazy(() => import("./pages/NotFound"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const JobsHistory = lazy(() => import("./pages/JobsHistory"));

// Configure QueryClient with error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        const message = error instanceof Error ? error.message : "An unexpected error occurred";
        toast.error("Operation failed", { description: message });
      },
    },
  },
});

const pageVariants = {
  initial: { opacity: 0, y: 6 },
  enter: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
};

const pageTransition = {
  duration: 0.25,
  ease: [0.4, 0, 0.2, 1] as const,
};

function AnimatedRoutes() {
  const location = useLocation();
  
  // Prefetch adjacent routes for instant navigation
  usePrefetch();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial="initial"
        animate="enter"
        exit="exit"
        variants={pageVariants}
        transition={pageTransition}
      >
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes location={location}>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/checkout" element={
              <RouteErrorBoundary routeName="Checkout">
                <Checkout />
              </RouteErrorBoundary>
            } />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/paywall" element={<Paywall />} />
            <Route path="/contact" element={<Contact />} />
            
            <Route path="/help" element={<Help />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/socials" element={<Socials />} />
            <Route path="/preview" element={
              <RouteErrorBoundary routeName="Preview">
                <Preview />
              </RouteErrorBoundary>
            } />

            {/* Protected routes with DashboardLayout */}
            <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="dashboard" element={
                <RouteErrorBoundary routeName="Dashboard">
                  <Dashboard />
                </RouteErrorBoundary>
              } />
              <Route path="ace" element={
                <RouteErrorBoundary routeName="Ace">
                  <Ace />
                </RouteErrorBoundary>
              } />
              <Route path="courses" element={
                <RouteErrorBoundary routeName="Courses">
                  <Courses />
                </RouteErrorBoundary>
              } />
              <Route path="course/:courseId" element={
                <RouteErrorBoundary routeName="Course Details">
                  <CourseDetail />
                </RouteErrorBoundary>
              } />
              <Route path="calendar" element={
                <RouteErrorBoundary routeName="Calendar">
                  <CalendarView />
                </RouteErrorBoundary>
              } />
              <Route path="grades" element={
                <RouteErrorBoundary routeName="Grades">
                  <Grades />
                </RouteErrorBoundary>
              } />
              <Route path="materials" element={
                <RouteErrorBoundary routeName="Materials">
                  <Materials />
                </RouteErrorBoundary>
              } />
              <Route path="materials/:materialId" element={
                <RouteErrorBoundary routeName="Material Details">
                  <MaterialDetail />
                </RouteErrorBoundary>
              } />
              <Route path="account" element={
                <RouteErrorBoundary routeName="Account">
                  <Account />
                </RouteErrorBoundary>
              } />
              <Route path="upload" element={
                <RouteErrorBoundary routeName="Upload">
                  <Upload />
                </RouteErrorBoundary>
              } />
              <Route path="jobs" element={
                <RouteErrorBoundary routeName="Jobs History">
                  <JobsHistory />
                </RouteErrorBoundary>
              } />
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <GlobalMutationIndicator />
          <OfflineIndicator />
          <Toaster />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Suspense fallback={<LoadingFallback />}>
              <AnimatedRoutes />
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
